// @givmo/auth — the account-bound passkey RE-CONFIRMATION (AS-WEB10; D-AS-17, backend SEC2-AS).
//
// A signed-in person proves, with one of THEIR OWN passkeys, that they are still at the keyboard:
//
//   POST /users/me/passkeys/reauthentication/options  →  navigator.credentials.get()  →
//   POST /users/me/passkeys/reauthentication          →  { custom_token }             →
//   the token's account checked against the signed-in one  →  signInWithCustomToken  →  getIdToken(true)
//
// The new session is a direct custom-token session for the SAME account: `gvm_amr: ["passkey_uv"]`,
// a fresh `auth_time`, no handed-off mirror. That fresh `auth_time` is what satisfies the step-up
// gate, the factor rules' 10-minute passkey window and Board's signing window (law 8 holds: this is a
// genuine credential ceremony verified server-side, not a handoff).
//
// ══ WHY A CUSTOM-TOKEN SIGN-IN IS ALLOWED HERE, AND NOWHERE ELSE ON A RE-AUTH PATH ════════════════
//
// `passkeys.ts` says a passkey must never be a step-up, and for the UNBOUND sign-in that stays true:
// its options name no account, so whoever asserts becomes the signed-in user and the retried act runs
// as them. This route is bound at both ends, and the binding is the whole design (D-AS-17, the one
// passkey "Confirm it's you"):
//   1. the options' `allowCredentials` lists exactly the signed-in account's passkeys, so the
//      platform offers only those — never the picker of every Givmo account on the device;
//   2. the backend resolves the presented credential by its id AND the caller's user id in one query,
//      and mints for the CALLER's identity whatever credential was presented;
//   3. this module reads the minted token's account and compares it with the signed-in one BEFORE the
//      token is exchanged, so even a server that minted for someone else never swaps the session.
// The unbound `signInWithPasskey` / `adminSignInWithPasskey` therefore never fill a step-up slot:
// `index.test.ts` keeps the boundary (no module that fills one may name them).
//
// ⚠ THE CUSTOM TOKEN IS A BEARER CREDENTIAL — the `passkeys.ts` rule, unchanged: a local between the
// exchange and `signInWithCustomToken`, never on an object, an error or a log. The account check reads
// one claim out of it and keeps nothing else.
//
// ⚠ EXACTLY ONE EXCHANGE. One `navigator.credentials.get()`, one `POST …/reauthentication`, one
// `signInWithCustomToken`; a failure anywhere surfaces instead of retrying, because each leg is a
// single-use challenge or a user gesture, and a silent second ceremony is a fingerprint nobody asked
// for. The step-up gate's own retry runs after this resolves (its forced refresh included).
//
// What this module deliberately does NOT do: re-run the suite session login. That needs the
// property's remember-me choice and its sign-out cancel handle, which only the app's auth context
// holds, so the app runs it right after this resolves (`establishSuiteSession`).

import { signInWithCustomToken, signOut, type Auth, type UserCredential } from 'firebase/auth';
import { AuthError } from './errors';
import {
  PASSKEY_CEREMONY_FAILED,
  PASSKEY_USER_VERIFICATION_REQUIRED,
  PasskeyTransportError,
} from './passkeys';
import {
  base64UrlDecode,
  isWebAuthnSupported,
  serializeAssertion,
  toRequestOptions,
  type AssertionCredentialLike,
  type PasskeyAssertionJson,
  type WebAuthnScope,
} from './webauthn';

/**
 * The backend's machine `error_code` when the account holds no passkey to confirm with (400 at the
 * options route). It is the caller's own state, so naming it leaks nothing.
 */
export const PASSKEY_NONE_REGISTERED = 'PASSKEY_NONE_REGISTERED';

/** Shown when the browser cannot run the ceremony at all. */
const PASSKEYS_UNAVAILABLE_MESSAGE = 'Passkeys are not available in this browser.';

/**
 * The two authenticated routes the re-confirmation rides on, injected as a port like
 * {@link PasskeyAssertionTransport}: this package stays free of HTTP coupling. Both carry the signed-in
 * account's Bearer token — the binding is the point.
 */
export interface PasskeyReconfirmationTransport {
  /**
   * `POST /users/me/passkeys/reauthentication/options` → the raw `PublicKeyCredentialRequestOptionsJSON`
   * (its `allowCredentials` = exactly this account's passkeys). Rejects with a
   * {@link PasskeyTransportError} carrying the backend's `error_code` — `PASSKEY_NONE_REGISTERED` when
   * the account holds none.
   */
  requestReconfirmationOptions(init?: { signal?: AbortSignal }): Promise<unknown>;
  /**
   * `POST /users/me/passkeys/reauthentication` → the Firebase custom token for the SAME account.
   * Built from the error envelope only on a refusal, never from the success body.
   */
  exchangeReconfirmation(
    assertion: PasskeyAssertionJson,
    init?: { signal?: AbortSignal },
  ): Promise<string>;
}

/** Injectable seams, as {@link PasskeySignInOptions}. */
export interface PasskeyReconfirmationOptions {
  /** Defaults to `navigator.credentials`. */
  credentials?: Pick<CredentialsContainer, 'get'>;
  /** Defaults to `globalThis`. */
  scope?: WebAuthnScope;
  /** Aborts the ceremony; surfaces as `cancelled`. */
  signal?: AbortSignal;
}

/**
 * The account a Firebase custom token names (its `uid` claim), or `null` when the token is not a
 * readable JWT. Reads the payload only: the signature is the backend's and Firebase's to verify, and
 * this check exists to refuse BEFORE anything is exchanged, not to authenticate the token.
 */
function customTokenAccount(token: string): string | null {
  const parts = token.split('.');
  if (parts.length !== 3 || parts[1] === undefined) return null;
  try {
    const payload: unknown = JSON.parse(new TextDecoder().decode(base64UrlDecode(parts[1])));
    const uid = (payload as { uid?: unknown } | null)?.uid;
    return typeof uid === 'string' && uid.length > 0 ? uid : null;
  } catch {
    return null;
  }
}

/** Map a refusal from either route onto the error model, by machine code — never by message. */
function routeFailure(error: unknown): AuthError {
  if (error instanceof PasskeyTransportError) {
    if (error.errorCode === PASSKEY_NONE_REGISTERED) {
      return new AuthError('passkeyNoneRegistered', { code: error.errorCode });
    }
    if (error.errorCode === PASSKEY_USER_VERIFICATION_REQUIRED) {
      return new AuthError('passkeyUserVerificationRequired', { code: error.errorCode });
    }
    if (error.errorCode === PASSKEY_CEREMONY_FAILED) {
      return new AuthError('passkeyCeremonyFailed', { code: error.errorCode });
    }
    if (error.status === 429) {
      return new AuthError('tooManyRequests', { code: error.errorCode ?? undefined });
    }
  }
  // Anything else keeps the server's own render-safe sentence (`COMPANY_SSO_REQUIRED` among them).
  return AuthError.from(error);
}

/** A dismissal (or WebAuthn's deliberately ambiguous `NotAllowedError`) is `cancelled`; else failed. */
function ceremonyFailure(error: unknown): AuthError {
  const name =
    typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : '';
  if (name === 'NotAllowedError' || name === 'AbortError') return new AuthError('cancelled');
  return new AuthError('passkeyCeremonyFailed');
}

/**
 * Re-confirm the signed-in account with one of its own passkeys, re-issuing the session under the
 * same account with a fresh `auth_time`, and resolve with the new credential once its ID token has
 * been force-refreshed.
 *
 * Rejects with an {@link AuthError}, and the session is untouched on every rejection before the
 * exchange:
 * - `sessionExpired` — nobody is signed in;
 * - `passkeyNoneRegistered` — the account holds no passkey (no ceremony is started);
 * - `cancelled` — the person dismissed the browser's prompt, or the ceremony was aborted;
 * - `passkeyCeremonyFailed` / `passkeyUserVerificationRequired` — the ceremony or its verification
 *   failed, or the minted token could not be read (nothing is exchanged);
 * - `userMismatch` — the minted token names another account, or another account signed in here
 *   while the ceremony ran: nothing is exchanged;
 * - anything else keeps the server's sentence (`COMPANY_SSO_REQUIRED`, a rate limit, a network fault).
 */
export async function reconfirmWithPasskey(
  auth: Auth,
  transport: PasskeyReconfirmationTransport,
  options: PasskeyReconfirmationOptions = {},
): Promise<UserCredential> {
  const expectedUid = auth.currentUser?.uid ?? null;
  if (expectedUid === null) throw new AuthError('sessionExpired');

  const scope = options.scope ?? globalThis;
  const credentials = options.credentials ?? globalThis.navigator?.credentials;
  if (!isWebAuthnSupported(scope) || credentials === undefined) {
    throw new AuthError('passkeyCeremonyFailed', { message: PASSKEYS_UNAVAILABLE_MESSAGE });
  }

  // A fresh challenge bound to this account, per attempt.
  let requestOptions: PublicKeyCredentialRequestOptions;
  try {
    const document = await transport.requestReconfirmationOptions({ signal: options.signal });
    requestOptions = toRequestOptions(document, scope);
  } catch (error) {
    throw routeFailure(error);
  }

  let credential: AssertionCredentialLike | null;
  try {
    credential = (await credentials.get({
      publicKey: requestOptions,
      signal: options.signal,
    })) as AssertionCredentialLike | null;
  } catch (error) {
    throw ceremonyFailure(error);
  }
  if (credential === null) throw new AuthError('cancelled');

  // ⚠ The custom token exists only between these statements. Do not hoist, store, log or attach it.
  let userCredential: UserCredential;
  try {
    const customToken = await transport.exchangeReconfirmation(serializeAssertion(credential), {
      signal: options.signal,
    });
    // THE ACCOUNT CHECK, BEFORE THE EXCHANGE: the token must name the account that asked, and that
    // account must still be the one signed in here (a sign-in in another tab during the ceremony
    // would otherwise be replaced by this one). Either failing, nothing is signed in. A token whose
    // account cannot be read is a failed ceremony, not someone else's: it names no one.
    const tokenAccount = customTokenAccount(customToken);
    if (tokenAccount === null) throw new AuthError('passkeyCeremonyFailed');
    if (tokenAccount !== expectedUid || auth.currentUser?.uid !== expectedUid) {
      throw new AuthError('userMismatch');
    }
    userCredential = await signInWithCustomToken(auth, customToken);
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw routeFailure(error);
  }

  // Belt: should Firebase ever have signed in another account after all, end that session at once.
  if (userCredential.user.uid !== expectedUid) {
    await signOut(auth);
    throw new AuthError('userMismatch');
  }

  // The new session's token, forced, so nothing downstream can be served the refused one.
  try {
    await userCredential.user.getIdToken(true);
  } catch (error) {
    throw AuthError.from(error);
  }
  return userCredential;
}
